import React from "react";
import { Eye, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Partner } from "../../types";
import ActionMenu, { ActionMenuItem } from "../UI/ActionMenu";
import { partnerColorHex } from "../../constants/partnerColors";
import { PartnerColorDot } from "./PartnerColorPicker";

interface PartnerTableProps {
  partners: Partner[];
  /**
   * Show the VAT number column.
   *
   * The same mirror `PartnerForm.withBilling` draws, and for the same reason:
   * `vatNumber` is a field on `Partner` and does not exist on `Addresses`. The
   * Addresses page reuses this table, where the column could only ever render a
   * blank — an empty column reads as missing data rather than as a field that
   * was never part of the record.
   *
   * Defaults to true so the Partners page, which is what this table was written
   * for, needs to say nothing.
   */
  withVat?: boolean;
  /**
   * Show each partner's tag colour beside its name. Off by default for the same
   * reason as `withVat`: the Addresses page reuses this table and an address
   * has no colour.
   */
  withColor?: boolean;
  selectedIds: string[];
  onSelectAll: (checked: boolean) => void;
  onSelectOne: (id: string, checked: boolean) => void;
  onEdit: (partner: Partner) => void;
  onDelete: (id: string) => void;
  loading: boolean;
}

export const PartnerTable: React.FC<PartnerTableProps> = ({
  partners,
  withVat = true,
  withColor = false,
  onEdit,
  onDelete,
  loading,
}) => {
  const { t } = useTranslation(["partners", "common"]);

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-12 bg-gray-100 border-b border-gray-200"></div>
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="h-16 bg-gray-50 border-b border-gray-100"
          ></div>
        ))}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto max-h-[50svh] md:max-h-[55vh] lg:max-h-[60vh] xl:max-h-[70vh] overflow-y-scroll border-b border-b-gray-300">
      <table className="min-w-full border-collapse border border-gray-300">
        <thead className="bg-gray-50 text-nowrap sticky top-0 z-1">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap border border-gray-200">
              {t("partners:table.id")}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap border border-gray-200">
              {t("partners:table.registrationCode")}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap border border-gray-200">
              {t("partners:table.clientCode")}
            </th>
            {withVat && (
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap border border-gray-200">
                {t("partners:table.vatNumber")}
              </th>
            )}
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap border border-gray-200">
              {t("partners:table.companyName")}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap border border-gray-200">
              {t("partners:table.emailAddress")}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap border border-gray-200">
              {t("partners:table.phoneNumber")}
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-200">
              {t("common:labels.actions")}
            </th>
          </tr>
        </thead>

        <tbody className="bg-white">
          {partners.map((partner) => (
            <tr
              key={partner._id}
              className="hover:bg-gray-50 transition-colors duration-150"
            >
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-medium border border-gray-200">
                {partner._id.slice(-6).toUpperCase()}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-medium border border-gray-200">
                {partner?.registerationCode || ""}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-medium border border-gray-200">
                {partner?.clientCode || ""}
              </td>
              {withVat && (
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-medium border border-gray-200">
                  {partner?.vatNumber || ""}
                </td>
              )}
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-500 border border-gray-200">
                {withColor ? (
                  <span className="inline-flex items-center gap-2">
                    <PartnerColorDot color={partnerColorHex(partner.color)} />
                    {partner.companyName}
                  </span>
                ) : (
                  partner.companyName
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border border-gray-200">
                {partner.emailAddress || t("partners:table.na")}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border border-gray-200">
                {partner.phoneNumber || t("partners:table.na")}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium border border-gray-200">
                <div className="flex items-center justify-end">
                  <ActionMenu
                    ariaLabel={t("common:labels.actions")}
                    items={
                      [
                        {
                          label: t("common:buttons.view"),
                          icon: <Eye className="w-4 h-4" />,
                          onClick: () => onEdit(partner),
                        },
                        {
                          label: t("common:buttons.delete"),
                          icon: <Trash2 className="w-4 h-4" />,
                          onClick: () => onDelete(partner._id),
                          variant: "danger",
                          divider: true,
                        },
                      ] as ActionMenuItem[]
                    }
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {partners.length === 0 && !loading && (
        <div className="text-center py-12">
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {t("partners:table.noRecords")}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {t("partners:table.getStarted")}
          </p>
        </div>
      )}
    </div>
  );
};
